"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted/30" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-lg bg-muted/30" />
        <div className="mt-8 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} data-index={i} className="h-4 animate-pulse rounded bg-muted/30" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-6 py-16">
      <header className="space-y-6">
        <h1 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8">
                {post.author.avatar?.["48"] && (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                )}
                <AvatarFallback>
                  {post.author.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}

          {post.date && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </time>
              </div>
            </>
          )}

          {post.categories && post.categories.length > 0 && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex flex-wrap gap-2">
                {post.categories.map((cat, i) => (
                  <Link
                    key={cat.id}
                    data-index={i}
                    to={cat.link || "#"}
                    className="rounded-md bg-muted px-2 py-1 text-xs font-medium transition-colors hover:bg-muted/80"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mt-8">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full rounded-lg object-cover shadow-medium"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg prose-gray mx-auto mt-12 max-w-none font-sans dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Tags:</span>
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              data-index={i}
              to={tag.link || "#"}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm transition-colors hover:bg-muted"
            >
              {tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {post.author && (
        <Card className="border-border shadow-soft">
          <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-start">
            <Avatar className="size-16 shrink-0">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="text-lg">
                {post.author.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <User className="size-4 text-muted-foreground" />
                <h3 className="font-display text-xl font-semibold">{post.author.name}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Professional wedding photographer and videographer capturing your special moments with elegance and care.
              </p>
              {post.author.link && (
                <Link
                  to={post.author.link}
                  className="inline-block text-sm text-primary transition-colors hover:text-primary-hover"
                >
                  View all posts by {post.author.name} →
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading || !posts || posts.length === 0) {
    return null;
  }

  return (
    <section className="border-t border-border bg-muted/30 py-16">
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="font-display text-3xl font-bold md:text-4xl">Related Posts</h2>
        <p className="mt-2 text-muted-foreground">Continue exploring our latest stories</p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.slice(0, 3).map((post, i) => (
            <Card key={post.id} data-index={i} className="group overflow-hidden border-border shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium">
              {post.featuredImage && (
                <div className="overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              )}
              <CardHeader>
                <CardTitle className="line-clamp-2 font-display text-xl group-hover:text-primary">
                  <Link to={post.link || "#"} className="transition-colors">
                    {post.title}
                  </Link>
                </CardTitle>
                {post.excerpt && (
                  <CardDescription className="line-clamp-3" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                )}
              </CardHeader>
              <CardContent>
                {post.link && (
                  <Link
                    to={post.link}
                    className="inline-flex items-center text-sm font-medium text-primary transition-colors hover:text-primary-hover"
                  >
                    Read more →
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = typeof window !== "undefined" && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentPostID() 
    : null;

  if (!currentPostId) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <p className="text-muted-foreground">Unable to load post.</p>
      </div>
    );
  }

  return (
    <>
      <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      <PostsProvider wp_query={{ per_page: 3, exclude: [currentPostId], order: "desc", orderby: "date" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
        <RelatedPostsContent />
      </PostsProvider>
    </>
  );
}